import React, { forwardRef, Ref, useImperativeHandle, useRef, useState } from 'react';
import styled from 'styled-components';

import { ModalSize } from 'src/presentation/components/pages/shared/EmbedModal';
import EmbedModalWithGreenHeader from 'src/presentation/components/pages/shared/EmbedModalWithGreenHeader';
import StyledFooter from 'src/presentation/components/forms/Footer';
import { Button, ButtonTypes } from 'src/presentation/components/agent_desktop_components';
import { getGladOsOrigin } from 'src/presentation/components/pages/helpers';
import { GuideInfo } from 'src/presentation/components/pages/glados/actionAdapters/ActionAdapters';
import { ActionAdapter } from 'src/presentation/components/pages/glados/actionAdapters/ActionAdapters';

type ShowModalProps = {
  params?: unknown;
  subscribe: (params: unknown) => void;
};

export type ModalWrapperRef = {
  show: ({ params, subscribe }: ShowModalProps) => void;
  close: () => void;
};

const GuidesModal = forwardRef((_, ref: Ref<ModalWrapperRef>) => {
  const [modalVisible, setModalVisible] = useState(false);
  const refCallbacks = useRef<ShowModalProps | null>(null);

  useImperativeHandle(ref, () => ({
    show(props) {
      refCallbacks.current = props;
      setModalVisible(true);
    },
    close: () => setModalVisible(false),
  }));

  const handleClose = () => {
    setModalVisible(false);
    refCallbacks.current = null;
  };

  if (!modalVisible) return null;

  let guides: GuideInfo[] | undefined = [];
  let actionAdapter: ActionAdapter | null = null;

  if (
    refCallbacks.current?.params &&
    typeof refCallbacks.current?.params === 'object' &&
    'actionAdapter' in refCallbacks.current?.params &&
    'guides' in refCallbacks.current?.params
  ) {
    guides = refCallbacks.current?.params.guides as GuideInfo[];
    actionAdapter = refCallbacks.current?.params.actionAdapter as ActionAdapter;
  }

  return (
    <StyledGreenHeaderModal
      content={<GuidesModalForm onClose={handleClose} actionAdapter={actionAdapter} guides={guides} />}
      disableOutsideClick={true}
      modalSize={ModalSize.MEDIUM}
      onCancel={handleClose}
      title="In Guide"
    />
  );
});

interface GuidesModalFormProps {
  onClose: () => void;
  actionAdapter: ActionAdapter | null;
  guides: GuideInfo[] | undefined;
}

const GuidesModalForm: React.FC<GuidesModalFormProps> = ({ onClose, actionAdapter, guides }) => {
  const handleClose = () => onClose();

  return (
    <>
      <ModalContent>
        <ModalDescription>
          <strong>{actionAdapter?.name}</strong> is used in the following Guides:
        </ModalDescription>
        <ScrollableModalDescription>
          <GuidesLinkList guides={guides} />
        </ScrollableModalDescription>
      </ModalContent>
      <RoundedFooter>
        <Button data-aid="modal-close" onClick={handleClose} type={ButtonTypes.PRIMARY}>
          Close
        </Button>
      </RoundedFooter>
    </>
  );
};

const GuidesLinkList = ({ guides }) => {
  const gladosOrigin = getGladOsOrigin(window.location.origin);
  return (
    <LinkSection>
      {guides.map(guide => (
        <GuideSection key={guide.id}>
          <GuideName>{guide.name}</GuideName>
          {guide.guides.map(thread => (
            <GladlyLink
              href={`${gladosOrigin}/sidekick/guides/${guide.id}/txguides/${thread.id}`}
              key={thread.id}
              target="_blank"
              rel="noreferrer"
            >
              {thread.name}
            </GladlyLink>
          ))}
        </GuideSection>
      ))}
    </LinkSection>
  );
};

const StyledGreenHeaderModal = styled(EmbedModalWithGreenHeader)`
  padding: 0;
`;

const RoundedFooter = styled(StyledFooter)`
  border-radius: 0 0 ${p => p.theme.borderRadius.large} ${p => p.theme.borderRadius.large};
  border-top: 1px solid ${p => p.theme.colors.gray100};
  gap: ${p => p.theme.spacing.medium};
`;

const LinkSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  padding: 0 ${p => p.theme.spacing.insetLarge} ${p => p.theme.spacing.insetLarge} ${p => p.theme.spacing.insetLarge};
  line-height: ${p => p.theme.lineHeight.base};
`;

const GuideSection = styled.div`
  align-items: start;
  display: flex;
  flex-direction: column;
  margin-bottom: ${p => p.theme.spacing.medium};

  &:last-child {
    margin-bottom: 0;
  }
`;

const GuideName = styled.div`
  color: ${p => p.theme.colors.black};
  font-size: ${p => p.theme.fontSize.base};
  margin-bottom: ${p => p.theme.spacing.small};
`;

const GladlyLink = styled.a`
  color: ${p => p.theme.colors.green400};
  text-decoration: none;
  margin-bottom: ${p => p.theme.spacing.small};

  &:hover {
    text-decoration: underline;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const ModalDescription = styled.div`
  align-self: stretch;
  color: ${p => p.theme.colors.black};
  font-style: normal;
  font-weight: ${p => p.theme.fontWeight.normal};
  line-height: ${p => p.theme.lineHeight.base};
  padding: ${p => p.theme.spacing.insetLarge};
  text-align: justify;
`;

const ModalContent = styled.div`
  max-height: 400px;
  overflow-y: auto;
`;

const ScrollableModalDescription = styled(ModalDescription)`
  max-height: 300px;
  overflow-y: auto;
`;

export default GuidesModal;
